import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ChatAttachment,
  ChatMessage,
  ChatSession,
  ChatContext,
  PinnedPage,
  SelectedTextContext,
  CostAnalysis,
  TokenBreakdown,
  TokenTypeMeta,
  VoiceTokenUsage,
  MessageMetadata,
} from '../models/chat.models';

/**
 * State management service for chat feature.
 *
 * Uses BehaviorSubject for reactive state management.
 */

export interface ContextFill {
  promptTokens: number;
  contextWindowSize: number;
  fillPercent: number;
}

/**
 * A message waiting to be sent once the current turn finishes (Issue #1746).
 *
 * The attachments are captured here rather than re-read from the composer at send time. That is
 * what makes a queued turn immutable: attaching or removing another image afterwards changes the
 * message being composed, and can never reach back into one that is already waiting in the queue.
 */
export interface QueuedMessage {
  text: string;
  attachments: ChatAttachment[];
}

export interface ChatState {
  currentSession: ChatSession | null;
  messages: ChatMessage[];
  isOpen: boolean;
  isDocked: boolean;
  isLoading: boolean;
  error: string | null;
  context: ChatContext;
  messageQueue: QueuedMessage[]; // Queued messages to send after current completes
  sessionCostAnalysis: CostAnalysis; // Accumulated cost analysis across session (Issue #691)
  pendingTurnCost: CostAnalysis | null; // Live preview of current turn cost before isFinal
  contextFill: ContextFill | null; // Latest context window fill level (Issue #919)
}

const LS_KEY_DOCKED = 'chat_is_docked';

const INITIAL_STATE: ChatState = {
  currentSession: null,
  messages: [],
  isOpen: false,
  isDocked: localStorage.getItem(LS_KEY_DOCKED) === 'true',
  isLoading: false,
  error: null,
  context: {},
  messageQueue: [],
  sessionCostAnalysis: { totalTokens: 0, estimatedTotalCostUsd: 0 },
  pendingTurnCost: null,
  contextFill: null,
};

/** Reactive state store for the AI chat panel: manages sessions, messages, loading/error states, token usage, and pinned pages. */
@Injectable({
  providedIn: 'root',
})
export class ChatStateService {
  private state$ = new BehaviorSubject<ChatState>(INITIAL_STATE);

  /**
   * Observable state stream
   */
  get state(): Observable<ChatState> {
    return this.state$.asObservable();
  }

  /**
   * Get current state value
   */
  get currentState(): ChatState {
    return this.state$.value;
  }

  /**
   * Update state
   */
  private updateState(updates: Partial<ChatState>) {
    this.state$.next({
      ...this.currentState,
      ...updates,
    });
  }

  /**
   * Set current session
   */
  setSession(session: ChatSession) {
    this.updateState({ currentSession: session });
  }

  /**
   * Set messages
   */
  setMessages(messages: ChatMessage[]) {
    this.updateState({ messages: messages || [] });
  }

  /**
   * Add message
   */
  addMessage(message: ChatMessage) {
    this.updateState({
      messages: [...this.currentState.messages, message],
    });
  }

  /**
   * Update last message (for streaming)
   * Replaces content with full accumulated response (ADK pattern)
   * Backend sends complete content each time, making frontend simpler and more resilient
   */
  updateLastMessage(fullContent: string, messageId?: string, streaming?: boolean) {
    const messages = [...this.currentState.messages];

    // If messageId provided, find that specific message
    // Otherwise use last assistant message (backward compat)
    let targetIndex = -1;

    if (messageId) {
      targetIndex = messages.findIndex(m => m.messageId === messageId);
      ClientLoggingService.log('INFO', '[State] Updating message by ID:', messageId, 'found at index:', targetIndex);
    } else {
      // Find last assistant message (old behavior)
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'ASSISTANT') {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex >= 0) {
      messages[targetIndex] = {
        ...messages[targetIndex],
        content: fullContent,
        ...(streaming === undefined ? {} : { streaming }),
      };
      this.updateState({ messages: messages || [] });
    } else {
      ClientLoggingService.log('ERROR', '[State] Could not find message to update:', messageId || 'last');
    }
  }

  /**
   * Set or clear the progress note shown under a streaming message (Issue #2989).
   * The backend sends one whenever the turn has been silent for a few seconds.
   */
  setMessageProgress(messageId: string, progress: string | undefined) {
    const messages = [...this.currentState.messages];
    const targetIndex = messages.findIndex(m => m.messageId === messageId);
    if (targetIndex < 0 || messages[targetIndex].progress === progress) {
      return;
    }
    messages[targetIndex] = { ...messages[targetIndex], progress };
    this.updateState({ messages });
  }

  /**
   * Update last message metadata (for thinking and tool calls)
   */
  updateLastMessageMetadata(metadata: Partial<MessageMetadata>, messageId?: string) {
    const messages = [...this.currentState.messages];

    // Find target message by ID, or use last assistant
    let targetIndex = -1;
    if (messageId) {
      targetIndex = messages.findIndex(m => m.messageId === messageId);
    } else {
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'ASSISTANT') {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex >= 0) {
      const newMetadata: MessageMetadata = {
        ...messages[targetIndex].metadata,
        ...metadata,
      };

      // Create new message object (Immutable update for OnPush)
      messages[targetIndex] = {
        ...messages[targetIndex],
        metadata: newMetadata,
      };

      this.updateState({ messages: messages || [] });
    }
  }

  /**
   * Set loading state
   */
  setLoading(isLoading: boolean) {
    this.updateState({ isLoading });
  }

  /**
   * Queue a message to send after current request completes
   */
  queueMessage(message: QueuedMessage) {
    const queue = [...this.currentState.messageQueue, message];
    this.updateState({ messageQueue: queue });
  }

  /**
   * Get and remove next queued message
   */
  dequeueMessage(): QueuedMessage | null {
    const queue = [...this.currentState.messageQueue];
    if (queue.length === 0) return null;

    const message = queue.shift()!;
    this.updateState({ messageQueue: queue });
    return message;
  }

  /**
   * Clear message queue
   */
  clearQueue() {
    this.updateState({ messageQueue: [] });
  }

  /**
   * Recalculate session cost analysis from loaded messages (e.g., after session switch or reload).
   */
  recalculateSessionCostAnalysis(messages: ChatMessage[]) {
    const totals: CostAnalysis = { totalTokens: 0, estimatedTotalCostUsd: 0 };
    let lastContextWindowSize = 0;
    let lastPromptTokens = 0;

    for (const msg of messages || []) {
      const ca = msg.metadata?.costAnalysis;
      if (ca && ((ca.totalTokens ?? 0) > 0 || ca.tokenBreakdown || ca.model)) {
        totals.totalTokens = (totals.totalTokens ?? 0) + (ca.totalTokens ?? 0);
        totals.estimatedTotalCostUsd = (totals.estimatedTotalCostUsd ?? 0) + (ca.estimatedTotalCostUsd ?? 0);
        totals.tokenBreakdown = this.mergeBreakdown(totals.tokenBreakdown, ca.tokenBreakdown);
        if (ca.contextWindowSize && ca.contextWindowSize > 0) {
          lastContextWindowSize = ca.contextWindowSize;
          totals.contextWindowSize = ca.contextWindowSize;
        }
        const inputTokens =
          (ca.tokenBreakdown?.nonCachedInput?.tokenCount ?? 0) + (ca.tokenBreakdown?.cachedContent?.tokenCount ?? 0);
        if (inputTokens > 0) {
          lastPromptTokens = inputTokens;
        }
      }
    }

    const updates: Partial<ChatState> = { sessionCostAnalysis: totals };

    if (lastContextWindowSize > 0 && lastPromptTokens > 0) {
      updates.contextFill = {
        promptTokens: lastPromptTokens,
        contextWindowSize: lastContextWindowSize,
        fillPercent: Math.round((lastPromptTokens / lastContextWindowSize) * 100),
      };
    } else {
      updates.contextFill = null;
    }

    this.updateState(updates);
  }

  /**
   * Add cost analysis from a completed message turn to the session total.
   * Also updates context fill when contextWindowSize is available.
   */
  addCostAnalysis(cost: CostAnalysis) {
    const current = this.currentState.sessionCostAnalysis;
    const updates: Partial<ChatState> = {
      sessionCostAnalysis: {
        totalTokens: (current.totalTokens ?? 0) + (cost.totalTokens ?? 0),
        estimatedTotalCostUsd: (current.estimatedTotalCostUsd ?? 0) + (cost.estimatedTotalCostUsd ?? 0),
        tokenBreakdown: this.mergeBreakdown(current.tokenBreakdown, cost.tokenBreakdown),
        contextWindowSize: cost.contextWindowSize ?? current.contextWindowSize,
        model: cost.model ?? current.model,
      },
    };
    const inputTokens =
      (cost.tokenBreakdown?.nonCachedInput?.tokenCount ?? 0) + (cost.tokenBreakdown?.cachedContent?.tokenCount ?? 0);
    if (cost.contextWindowSize && cost.contextWindowSize > 0 && inputTokens > 0) {
      updates.contextFill = {
        promptTokens: inputTokens,
        contextWindowSize: cost.contextWindowSize,
        fillPercent: Math.round((inputTokens / cost.contextWindowSize) * 100),
      };
    }
    this.updateState(updates);
  }

  /**
   * Set a live preview of the current turn's cost during streaming (before isFinal).
   * The pending cost is displayed on top of the accumulated session cost so counters
   * update in real time. Call clearPendingTurnCost() when the turn finalizes.
   */
  setPendingTurnCost(cost: CostAnalysis) {
    this.updateState({ pendingTurnCost: cost });
  }

  clearPendingTurnCost() {
    this.updateState({ pendingTurnCost: null });
  }

  /**
   * Set absolute token usage from a voice chat session.
   * Unlike addCostAnalysis() which accumulates deltas, this replaces the values —
   * the Live API reports the current total context state, not per-turn increments.
   */
  setVoiceTokenUsage(usage: VoiceTokenUsage) {
    const updates: Partial<ChatState> = {
      sessionCostAnalysis: {
        totalTokens: usage.totalTokens,
        estimatedTotalCostUsd: usage.estimatedTotalCostUsd ?? 0,
        model: usage.model,
        contextWindowSize: usage.contextWindowSize,
        tokenBreakdown: {
          nonCachedInput: { tokenCount: usage.promptTokens },
          output: { tokenCount: usage.candidatesTokens },
        },
      },
    };
    if (usage.contextWindowSize > 0) {
      updates.contextFill = {
        promptTokens: usage.promptTokens,
        contextWindowSize: usage.contextWindowSize,
        fillPercent: usage.promptTokens > 0 ? Math.round((usage.promptTokens / usage.contextWindowSize) * 100) : 0,
      };
    } else {
      updates.contextFill = null;
    }
    this.updateState(updates);
  }

  /**
   * Recalculate context fill when switching models. Keeps the last known prompt token
   * count and recomputes the fill % against the new model's context window. If no prior
   * prompt data exists, shows 0% for the new model so the user sees the total capacity.
   */
  recalculateContextFillForModel(contextWindowSize: number) {
    if (!contextWindowSize || contextWindowSize <= 0) {
      this.updateState({ contextFill: null });
      return;
    }
    const promptTokens = this.currentState.contextFill?.promptTokens ?? 0;
    this.updateState({
      contextFill: {
        promptTokens,
        contextWindowSize,
        fillPercent: promptTokens > 0 ? Math.round((promptTokens / contextWindowSize) * 100) : 0,
      },
    });
  }

  /**
   * Clear context fill entirely (e.g., session reset).
   */
  clearContextFill() {
    this.updateState({ contextFill: null });
  }

  /**
   * Set error
   */
  setError(error: string | null) {
    this.updateState({ error });
  }

  /**
   * Toggle chat panel
   */
  toggleChat() {
    this.updateState({ isOpen: !this.currentState.isOpen });
  }

  openChat() {
    this.updateState({ isOpen: true });
  }

  closeChat() {
    this.updateState({ isOpen: false });
  }

  toggleDocked() {
    const docked = !this.currentState.isDocked;
    localStorage.setItem(LS_KEY_DOCKED, String(docked));
    this.updateState({ isDocked: docked });
  }

  /** Force docked state without toggling (used by snap-to-screen). */
  setDocked(docked: boolean) {
    if (this.currentState.isDocked === docked) return;
    localStorage.setItem(LS_KEY_DOCKED, String(docked));
    this.updateState({ isDocked: docked });
  }

  /**
   * Update context
   */
  updateContext(context: Partial<ChatContext>) {
    this.updateState({
      context: {
        ...this.currentState.context,
        ...context,
      },
    });
  }

  /**
   * Add pinned page
   */
  addPinnedPage(page: PinnedPage) {
    const currentPages = this.currentState.context.pinnedPages || [];

    // Don't add if already pinned
    const exists = currentPages.some(p => p.pageNumber === page.pageNumber && p.fileId === page.fileId);

    if (!exists) {
      this.updateContext({
        pinnedPages: [...currentPages, page],
      });
      this.savePinnedPages();
    }
  }

  /**
   * Remove pinned page
   */
  removePinnedPage(page: PinnedPage) {
    const currentPages = this.currentState.context.pinnedPages || [];
    this.updateContext({
      pinnedPages: currentPages.filter(p => !(p.pageNumber === page.pageNumber && p.fileId === page.fileId)),
    });
    this.savePinnedPages();
  }

  /**
   * Add selected text quote context (Issue #1745)
   */
  addSelectedText(text: SelectedTextContext) {
    const currentTexts = this.currentState.context.selectedTexts || [];
    const exists = currentTexts.some(
      t => t.fileId === text.fileId && t.pageNumber === text.pageNumber && t.selectedText === text.selectedText
    );
    if (!exists) {
      const id = text.id || `st-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      this.updateContext({
        selectedTexts: [...currentTexts, { ...text, id }],
      });
    }
  }

  /**
   * Remove selected text quote context (Issue #1745)
   */
  removeSelectedText(textOrId: SelectedTextContext | string) {
    const currentTexts = this.currentState.context.selectedTexts || [];
    const idToRemove = typeof textOrId === 'string' ? textOrId : textOrId.id;
    this.updateContext({
      selectedTexts: currentTexts.filter(t => (idToRemove ? t.id !== idToRemove : t !== textOrId)),
    });
  }

  /**
   * Clear all selected text quote contexts (Issue #1745)
   */
  clearSelectedTexts() {
    this.updateContext({
      selectedTexts: [],
    });
  }

  /**
   * Clear session
   */
  clearSession() {
    this.state$.next({
      ...INITIAL_STATE,
      context: this.currentState.context, // Preserve context
    });
  }

  /**
   * Reset session data (session, messages, tokens, context fill) while
   * preserving UI state (isOpen, isDocked, context, messageQueue).
   * Use when switching to a new/different session without closing the panel.
   */
  resetSessionData() {
    this.updateState({
      currentSession: null,
      messages: [],
      sessionCostAnalysis: { totalTokens: 0, estimatedTotalCostUsd: 0 },
      contextFill: null,
    });
  }

  /**
   * Save pinned pages to localStorage
   */
  private savePinnedPages() {
    try {
      const pages = this.currentState.context.pinnedPages || [];
      localStorage.setItem('chat_pinned_pages', JSON.stringify(pages));
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Failed to save pinned pages:', error);
    }
  }

  /**
   * Load pinned pages from localStorage
   */
  loadPinnedPages(): PinnedPage[] {
    try {
      const stored = localStorage.getItem('chat_pinned_pages');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Failed to load pinned pages:', error);
      return [];
    }
  }

  private mergeBreakdown(a?: TokenBreakdown, b?: TokenBreakdown): TokenBreakdown {
    if (!a) return b ?? {};
    if (!b) return a;
    return {
      nonCachedInput: this.mergeTokenType(a.nonCachedInput, b.nonCachedInput),
      cachedContent: this.mergeTokenType(a.cachedContent, b.cachedContent),
      output: this.mergeTokenType(a.output, b.output),
      thinking: this.mergeTokenType(a.thinking, b.thinking),
      toolUse: this.mergeTokenType(a.toolUse, b.toolUse),
    };
  }

  private mergeTokenType(a?: TokenTypeMeta, b?: TokenTypeMeta): TokenTypeMeta {
    if (!a) return b ?? {};
    if (!b) return a;
    return {
      tokenCount: (a.tokenCount ?? 0) + (b.tokenCount ?? 0),
      costUsd: (a.costUsd ?? 0) + (b.costUsd ?? 0),
      ratePerMillion: b.ratePerMillion ?? a.ratePerMillion,
    };
  }
}
